import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { listFees, getFeesSummary } from '../api/feesApi'
import { loadMasterOptions } from '../../students/api/mastersApi'
import { appToast } from '../../../../components/AppToast'

export const SEARCH_FIELD_OPTIONS = [
  { value: 'Name', label: 'Name' },
  { value: 'Roll No.', label: 'Roll No.' },
  { value: 'ID No.', label: 'ID No.' },
  { value: 'Phone No.', label: 'Phone No.' },
  { value: 'Receipt No.', label: 'Receipt No.' },
  { value: 'Cheque No.', label: 'Cheque No.' },
]

export const TYPE_OPTIONS = [
  { label: 'All', value: 'All' },
  { label: 'Paid', value: 'Paid' },
  { label: 'Balance', value: 'Balance' },
  { label: 'OverDue', value: 'OverDue' },
]

export const GENDER_OPTIONS = [
  { label: 'All', value: 'All' },
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
  { label: 'Other', value: 'Other' },
]

export const DATE_PERIOD_OPTIONS = [
  { value: 'Period', label: 'Period' },
  { value: 'Current Year', label: 'Current Year' },
  { value: 'Current Month', label: 'Current Month' },
  { value: 'Current Week', label: 'Current Week' },
  { value: 'Last Year', label: 'Last Year' },
  { value: 'Last Month', label: 'Last Month' },
  { value: 'Last Week', label: 'Last Week' },
  { value: 'Next Year', label: 'Next Year' },
  { value: 'Next Month', label: 'Next Month' },
  { value: 'Next Week', label: 'Next Week' },
  { value: 'Today', label: 'Today' },
  { value: 'Yesterday', label: 'Yesterday' },
]

export function useFeesListScreen() {
  const [searchParams] = useSearchParams()
  const studentId = searchParams.get('studentId')
  const fromStudent = searchParams.get('from') === 'students'

  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({ total: 0, paid: 0, balance: 0, overdue: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 25

  // Filters State
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [schemeFilter, setSchemeFilter] = useState('All')
  const [typeFilter, setTypeFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('All')
  const [datePeriodFilter, setDatePeriodFilter] = useState('Period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [subjectFilter, setSubjectFilter] = useState('All')
  const [genderFilter, setGenderFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('All')

  // Dynamic Master Lookups
  const [masterOptions, setMasterOptions] = useState({})

  useEffect(() => {
    loadMasterOptions([
      'institutes',
      'branches',
      'courses',
      'batches',
      'schemes',
      'academic_years',
      'subjects',
    ])
      .then(setMasterOptions)
      .catch(() => {})
  }, [])

  const companyOptions = useMemo(
    () => [{ label: 'All', value: 'All' }, ...(masterOptions.institutes || []).map((v) => ({ label: v, value: v }))],
    [masterOptions.institutes]
  )

  const branchOptions = useMemo(
    () => [{ label: 'All', value: 'All' }, ...(masterOptions.branches || []).map((v) => ({ label: v, value: v }))],
    [masterOptions.branches]
  )

  const courseOptions = useMemo(
    () => [{ label: 'All', value: 'All' }, ...(masterOptions.courses || []).map((v) => ({ label: v, value: v }))],
    [masterOptions.courses]
  )

  const batchOptions = useMemo(
    () => [{ label: 'All', value: 'All' }, ...(masterOptions.batches || []).map((v) => ({ label: v, value: v }))],
    [masterOptions.batches]
  )

  const schemeOptions = useMemo(
    () => [{ label: 'All', value: 'All' }, ...(masterOptions.schemes || []).map((v) => ({ label: v, value: v }))],
    [masterOptions.schemes]
  )

  const academicYearOptions = useMemo(
    () => [{ label: 'All', value: 'All' }, ...(masterOptions.academic_years || []).map((v) => ({ label: v, value: v }))],
    [masterOptions.academic_years]
  )

  const subjectOptions = useMemo(
    () => [{ label: 'All', value: 'All' }, ...(masterOptions.subjects || []).map((v) => ({ label: v, value: v }))],
    [masterOptions.subjects]
  )

  const loadFees = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await listFees({
        searchField,
        searchText,
        companyFilter,
        branchFilter,
        courseFilter,
        schemeFilter,
        typeFilter,
        batchFilter,
        academicYearFilter,
        subjectFilter,
        genderFilter,
        statusFilter,
        fromDate,
        toDate,
        studentId: studentId ? Number(studentId) : undefined,
      })
      setRows(Array.isArray(data) ? data : [])
      setCurrentPage(1)
    } catch (err) {
      console.error('Failed to load fees:', err)
      appToast.error('Failed to load fees records')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [
    searchField,
    searchText,
    companyFilter,
    branchFilter,
    courseFilter,
    schemeFilter,
    typeFilter,
    batchFilter,
    academicYearFilter,
    subjectFilter,
    genderFilter,
    statusFilter,
    fromDate,
    toDate,
    studentId,
  ])

  const loadSummary = useCallback(async () => {
    try {
      const data = await getFeesSummary({
        studentId: studentId ? Number(studentId) : undefined,
        companyFilter,
        branchFilter,
        courseFilter,
        batchFilter,
        schemeFilter,
        typeFilter,
        genderFilter,
        statusFilter,
        fromDate,
        toDate,
      })
      setSummary(data)
    } catch (err) {
      console.error('Failed to load summary:', err)
    }
  }, [
    studentId,
    companyFilter,
    branchFilter,
    courseFilter,
    batchFilter,
    schemeFilter,
    typeFilter,
    genderFilter,
    statusFilter,
    fromDate,
    toDate,
  ])

  useEffect(() => {
    loadFees()
    loadSummary()
  }, [loadFees, loadSummary])

  const formatDateISO = (d) => {
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handlePeriodChange = (period) => {
    setDatePeriodFilter(period)
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth()

    const fyStartYear = currentMonth >= 3 ? currentYear : currentYear - 1

    if (period === 'Current Year') {
      setFromDate(`${fyStartYear}-04-01`)
      setToDate(`${fyStartYear + 1}-03-31`)
    } else if (period === 'Last Year') {
      setFromDate(`${fyStartYear - 1}-04-01`)
      setToDate(`${fyStartYear}-03-31`)
    } else if (period === 'Next Year') {
      setFromDate(`${fyStartYear + 1}-04-01`)
      setToDate(`${fyStartYear + 2}-03-31`)
    } else if (period === 'Current Month') {
      const start = new Date(currentYear, currentMonth, 1)
      const end = new Date(currentYear, currentMonth + 1, 0)
      setFromDate(formatDateISO(start))
      setToDate(formatDateISO(end))
    } else if (period === 'Last Month') {
      const start = new Date(currentYear, currentMonth - 1, 1)
      const end = new Date(currentYear, currentMonth, 0)
      setFromDate(formatDateISO(start))
      setToDate(formatDateISO(end))
    } else if (period === 'Next Month') {
      const start = new Date(currentYear, currentMonth + 1, 1)
      const end = new Date(currentYear, currentMonth + 2, 0)
      setFromDate(formatDateISO(start))
      setToDate(formatDateISO(end))
    } else if (period === 'Current Week') {
      const day = now.getDay()
      const diffToMon = (day === 0 ? -6 : 1) - day
      const mon = new Date(now)
      mon.setDate(now.getDate() + diffToMon)
      const sun = new Date(mon)
      sun.setDate(mon.getDate() + 6)
      setFromDate(formatDateISO(mon))
      setToDate(formatDateISO(sun))
    } else if (period === 'Last Week') {
      const day = now.getDay()
      const diffToMon = (day === 0 ? -6 : 1) - day
      const lastMon = new Date(now)
      lastMon.setDate(now.getDate() + diffToMon - 7)
      const lastSun = new Date(lastMon)
      lastSun.setDate(lastMon.getDate() + 6)
      setFromDate(formatDateISO(lastMon))
      setToDate(formatDateISO(lastSun))
    } else if (period === 'Next Week') {
      const day = now.getDay()
      const diffToMon = (day === 0 ? -6 : 1) - day
      const nextMon = new Date(now)
      nextMon.setDate(now.getDate() + diffToMon + 7)
      const nextSun = new Date(nextMon)
      nextSun.setDate(nextMon.getDate() + 6)
      setFromDate(formatDateISO(nextMon))
      setToDate(formatDateISO(nextSun))
    } else if (period === 'Today') {
      const today = formatDateISO(now)
      setFromDate(today)
      setToDate(today)
    } else if (period === 'Yesterday') {
      const y = new Date(now)
      y.setDate(now.getDate() - 1)
      const yStr = formatDateISO(y)
      setFromDate(yStr)
      setToDate(yStr)
    } else {
      setFromDate('')
      setToDate('')
    }
  }

  const handleKpiCardClick = (selectedType) => {
    setTypeFilter((prev) => (prev === selectedType ? 'All' : selectedType))
  }

  const resetFilters = () => {
    setSearchField('Name')
    setSearchText('')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setSchemeFilter('All')
    setTypeFilter('All')
    setBatchFilter('All')
    setAcademicYearFilter('All')
    setDatePeriodFilter('Period')
    setFromDate('')
    setToDate('')
    setSubjectFilter('All')
    setGenderFilter('All')
    setStatusFilter('All')
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const paginatedRows = useMemo(() => {
    return rows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  }, [rows, currentPage, pageSize])

  return {
    state: {
      rows,
      summary,
      isLoading,
      currentPage,
      pageSize,
      totalPages,
      paginatedRows,
      fromStudent,
      studentId,
      searchField,
      searchText,
      companyFilter,
      branchFilter,
      courseFilter,
      schemeFilter,
      typeFilter,
      batchFilter,
      academicYearFilter,
      datePeriodFilter,
      fromDate,
      toDate,
      subjectFilter,
      genderFilter,
      statusFilter,
      options: {
        companyOptions,
        branchOptions,
        courseOptions,
        batchOptions,
        schemeOptions,
        academicYearOptions,
        subjectOptions,
        typeOptions: TYPE_OPTIONS,
        genderOptions: GENDER_OPTIONS,
        searchFieldOptions: SEARCH_FIELD_OPTIONS,
        datePeriodOptions: DATE_PERIOD_OPTIONS,
      },
    },
    actions: {
      setSearchField,
      setSearchText,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setSchemeFilter,
      setTypeFilter,
      setBatchFilter,
      setAcademicYearFilter,
      setDatePeriodFilter,
      setFromDate,
      setToDate,
      setSubjectFilter,
      setGenderFilter,
      setStatusFilter,
      setCurrentPage,
      handlePeriodChange,
      handleKpiCardClick,
      resetFilters,
      refresh: loadFees,
    },
  }
}
